import { useState, useRef, useEffect, useCallback } from 'react'
import { Sparkles, ArrowUpRight, AlertCircle, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { interviewService } from '@/services'


// ---------- Types ----------

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  variant?: 'warning'
}

interface GapFillViewProps {
  onComplete: () => void
}

// ---------- Component ----------

export function GapFillView({ onComplete }: GapFillViewProps) {
  // Chat state
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState('')
  const [currentGap, setCurrentGap] = useState(0)
  const [sending, setSending] = useState(false)
  const [attemptCounts, setAttemptCounts] = useState<Record<number, number>>({})
  const scrollRef = useRef<HTMLDivElement>(null)
  const completionTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // Backend session
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [backendGapIds, setBackendGapIds] = useState<string[]>([])
  const [backendGapDescriptions, setBackendGapDescriptions] = useState<string[]>([])
  const [backendGapQuestions, setBackendGapQuestions] = useState<string[]>([])
  const [totalGaps, setTotalGaps] = useState(0)
  const [mode, setMode] = useState<'loading' | 'backend' | 'error'>('loading')
  const [error, setError] = useState<string | null>(null)
  const didFetch = useRef(false);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: scrollRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages])

  // Clean up completion timer on unmount
  useEffect(() => {
    return () => {
      if (completionTimer.current) clearTimeout(completionTimer.current)
    }
  }, [])

  // Auto-resize textarea based on content
  useEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return

    textarea.style.height = 'auto'
    textarea.style.height = `${textarea.scrollHeight}px`
  }, [inputValue])

  // On mount: require backend-driven interview gaps
  useEffect(() => {
    async function init() {
      try {
        const res = await interviewService.startSession({ maxGaps: 3 })

        if (res.sessionId && res.targetGaps?.length) {
          setSessionId(res.sessionId)
          setBackendGapIds(res.targetGaps.map((g) => g.id))
          setBackendGapDescriptions(res.targetGaps.map((g) => g.description || ''))
          setBackendGapQuestions(res.targetGaps.map((g) => g.suggestedQuestion || ''))
          setTotalGaps(Math.min(res.targetGaps.length, 3))
          setMode('backend')

          // Use the backend's initial message or format with description + question
          let firstText: string
          if (res.initialMessage?.content) {
            firstText = res.initialMessage.content
          } else {
            const firstGap = res.targetGaps[0]
            const description = firstGap?.description || ''
            const question = firstGap?.suggestedQuestion || 'Tell me about your startup.'
            firstText = description
              ? `I've identified some gaps in your business context. Let's start with the first one.\n\n**${description}**\n\n${question}`
              : question
          }
          setMessages([{ id: 'ai-0', role: 'assistant', content: firstText }])
          return
        }

        setMessages([
          {
            id: 'ai-done-0',
            role: 'assistant',
            content: 'No critical gaps need follow-up right now. Let\'s proceed.',
          },
        ])
        setTotalGaps(0)
        completionTimer.current = setTimeout(onComplete, 1500)
        setMode('backend')
        return
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to connect to server.'
        setError(message)
        setMode('error')
        return
      }
    }
    if (didFetch.current) return;
    didFetch.current = true;

    init()
  }, [])

  /** Send the user's answer; stay on gap if unresolved, advance after 3 attempts max. */
  const handleSend = useCallback(async () => {
    const text = inputValue.trim()
    if (!text || sending) return

    // Append user message
    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: text,
    }
    setMessages((prev) => [...prev, userMsg])
    setInputValue('')
    setSending(true)

    try {
      let gapResolved = false
      let aiResponseText: string | undefined

      if (!sessionId) {
        throw new Error('Interview session is unavailable.')
      }

      const activeGapId = backendGapIds[currentGap]
      const res = await interviewService.legacySendMessage(sessionId, text, activeGapId)
      gapResolved = res.gapUpdates?.[activeGapId] === 'resolved'
      aiResponseText = res.message?.content

      // Track attempts (only counted on successful API calls)
      const attempts = (attemptCounts[currentGap] || 0) + 1
      setAttemptCounts((prev) => ({ ...prev, [currentGap]: attempts }))

      const maxAttemptsReached = attempts >= 3
      const shouldAdvance = gapResolved || maxAttemptsReached

      if (shouldAdvance) {
        const nextIdx = currentGap + 1

        // Show warning when forcing past an unresolved gap
        if (maxAttemptsReached && !gapResolved) {
          setMessages((prev) => [
            ...prev,
            {
              id: `ai-moveon-${Date.now()}`,
              role: 'assistant',
              content: "Moving on — we can revisit this area later. Let's continue.",
              variant: 'warning',
            },
          ])
        }

        if (nextIdx < totalGaps) {
          // Format the next gap message with description + question
          let nextText: string
          const description = backendGapDescriptions[nextIdx] || ''
          const question = backendGapQuestions[nextIdx] || 'Tell me about the next area.'
          nextText = description
            ? `Let's move on. **${description}**\n\n${question}`
            : question
          setMessages((prev) => [
            ...prev,
            { id: `ai-next-${Date.now()}`, role: 'assistant', content: nextText },
          ])
          setCurrentGap(nextIdx)
        } else {
          // All gaps done
          setMessages((prev) => [
            ...prev,
            {
              id: `ai-done-${Date.now()}`,
              role: 'assistant',
              content: "Excellent. That gives me a solid foundation. Let's proceed.",
            },
          ])
          completionTimer.current = setTimeout(onComplete, 1500)
        }
      } else {
        // Stay on same gap — show follow-up from AI
        const followUp = aiResponseText || 'Can you tell me more about this?'
        setMessages((prev) => [
          ...prev,
          { id: `ai-followup-${Date.now()}`, role: 'assistant', content: followUp },
        ])
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.'
      setError(message)
      setSending(false)
      return
    }

    setError(null)
    setSending(false)
  }, [inputValue, sending, sessionId, backendGapIds, backendGapDescriptions, backendGapQuestions, currentGap, totalGaps, onComplete, attemptCounts])

  // ---------- Render ----------

  return (
    <div className="flex flex-col rounded-xl border border-border bg-bg-elevated overflow-hidden min-h-[400px] max-h-[calc(100vh-280px)]">
      {/* Header */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-base font-semibold text-text-primary">
            Solidify Foundation
          </h2>
          <span className="font-mono text-xs text-text-tertiary bg-bg-surface px-2 py-1 rounded-md">
            {totalGaps === 0 ? 'No gaps' : `${Math.min(currentGap + 1, totalGaps)}/${totalGaps} Gaps`}
            {totalGaps > 0 && (attemptCounts[currentGap] || 0) > 0 && (
              <span className="text-accent-amber"> · Attempt {(attemptCounts[currentGap] || 0) + 1}/3</span>
            )}
          </span>
        </div>
        <p className="text-xs text-text-secondary">
          {totalGaps === 0
            ? 'The backend did not identify any follow-up gaps for this onboarding pass.'
            : `Addressing these ${totalGaps} core areas is required to build a reliable context graph.`}
        </p>
        <div className="mt-3 h-1 w-full rounded-full bg-bg-surface overflow-hidden">
          <div
            className="h-full bg-accent-amber rounded-full transition-all duration-500 ease-out"
            style={{ width: `${totalGaps === 0 ? 100 : (currentGap / totalGaps) * 100}%` }}
          />
        </div>
      </div>

      {/* Chat area */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-5 py-4 space-y-4"
      >
        {mode === 'loading' ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-6 w-6 animate-spin text-accent-amber" />
          </div>
        ) : mode === 'error' ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-4">
            <AlertCircle className="h-8 w-8 text-accent-error" />
            <p className="text-sm text-accent-error text-center">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-accent-amber hover:underline"
            >
              Retry
            </button>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                'flex gap-3 max-w-[85%]',
                msg.role === 'user' ? 'ml-auto flex-row-reverse' : ''
              )}
            >
              <div
                className={cn(
                  'h-7 w-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5',
                  msg.role === 'assistant'
                    ? 'bg-accent-amber/15 text-accent-amber'
                    : 'bg-bg-surface text-text-secondary'
                )}
              >
                {msg.role === 'assistant' ? (
                  <Sparkles className="h-3.5 w-3.5" />
                ) : (
                  <span className="text-[10px] font-bold">You</span>
                )}
              </div>
              <div
                className={cn(
                  'px-3.5 py-2.5 rounded-lg text-sm leading-relaxed',
                  msg.role === 'assistant'
                    ? msg.variant === 'warning'
                      ? 'bg-accent-amber/10 border border-accent-amber/20 text-text-primary'
                      : 'bg-bg-surface text-text-primary'
                    : 'bg-accent-amber text-text-on-amber'
                )}
              >
                {msg.content.split('\n').map((line, i) => (
                  <p key={i} className={i > 0 ? 'mt-2' : ''}>
                    {line.split('**').map((part, j) =>
                      j % 2 === 1 ? (
                        <strong key={j} className="font-semibold">
                          {part}
                        </strong>
                      ) : (
                        part
                      )
                    )}
                  </p>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Input area */}
      <div className="px-5 py-3 border-t border-border">
        {error && mode !== 'error' && (
          <p className="text-xs text-accent-error bg-accent-error/10 border border-accent-error/20 rounded-lg px-3 py-2 mb-2">
            {error}
          </p>
        )}
        <div className="relative flex items-end">
          <textarea
            ref={textareaRef}
            className="w-full rounded-lg border border-border bg-bg-surface px-4 py-3 pr-12 text-sm text-text-primary outline-none transition-colors placeholder:text-text-tertiary focus:border-accent-amber/50 focus:ring-1 focus:ring-accent-amber/30 resize-none overflow-hidden min-h-[44px] max-h-[200px]"
            placeholder="Type your answer..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSend()
              }
            }}
            disabled={mode === 'loading' || mode === 'error' || sending}
            autoFocus
            rows={1}
          />
          <div className="absolute right-2 bottom-2">
            <button
              aria-label="Send answer"
              onClick={handleSend}
              disabled={!inputValue.trim() || sending}
              className={cn(
                'p-2 rounded-md transition-colors',
                inputValue.trim() && !sending
                  ? 'bg-accent-amber text-text-on-amber hover:bg-accent-amber-hover'
                  : 'text-text-tertiary opacity-50 cursor-not-allowed'
              )}
            >
              <ArrowUpRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
